import { useState, useEffect, useRef } from 'react';
import Sidebar from './Sidebar';
import { Toaster } from 'sonner';
import { Menu, X, Bell, AlertTriangle, FileText, Receipt } from 'lucide-react';
import api from '../../lib/api';
import { Badge } from '../ui/badge';

const LEVEL_COLORS = { critical: 'bg-red-500', expired: 'bg-red-500', warning: 'bg-amber-500', info: 'bg-slate-400' };
const TYPE_ICONS = { budget: AlertTriangle, document: FileText, expense: Receipt };

export default function DashboardLayout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notifCount, setNotifCount] = useState(0);
  const [showNotifs, setShowNotifs] = useState(false);
  const notifRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/notifications');
        setNotifications(res.data.notifications || []);
        setNotifCount(res.data.count || 0);
      } catch {}
    };
    load();
    const interval = setInterval(load, 60000); // refresh every 60s
    return () => clearInterval(interval);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifs(false); };
    if (showNotifs) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showNotifs]);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 h-14 flex items-center justify-between px-4 border-b border-slate-200" style={{ background: '#0f172a' }}>
        <button onClick={() => setMobileOpen(true)} className="text-white p-1" data-testid="mobile-menu-btn"><Menu className="w-6 h-6" /></button>
        <span className="text-white font-bold text-base" style={{ fontFamily: 'Manrope' }}>Valtor</span>
        <div className="relative" ref={notifRef}>
          <button onClick={() => { setShowNotifs(!showNotifs); if (!showNotifs) setNotifCount(0); }} className="text-white p-1 relative">
            <Bell className="w-5 h-5" />
            {notifCount > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-[9px] text-white flex items-center justify-center font-bold">{notifCount}</span>}
          </button>
        </div>
      </div>

      {/* Desktop notification bell - fixed top right */}
      <div className="hidden md:block fixed top-4 right-6 z-40" ref={notifRef}>
        <button onClick={() => { setShowNotifs(!showNotifs); if (!showNotifs) setNotifCount(0); }} className="relative p-2 rounded-lg bg-white border border-slate-200 shadow-sm hover:shadow-md transition-all" data-testid="notification-bell">
          <Bell className="w-5 h-5 text-slate-600" />
          {notifCount > 0 && <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 rounded-full text-[10px] text-white flex items-center justify-center font-bold">{notifCount}</span>}
        </button>

        {/* Notification Dropdown */}
        {showNotifs && (
          <div className="absolute right-0 top-12 w-80 bg-white border border-slate-200 rounded-xl shadow-xl max-h-96 overflow-y-auto z-50">
            <div className="p-3 border-b border-slate-100">
              <p className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Notifications</p>
            </div>
            {notifications.length === 0 ? (
              <div className="p-6 text-center text-slate-400 text-sm">No notifications</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {notifications.slice(0, 15).map((n, i) => {
                  const Icon = TYPE_ICONS[n.type] || Bell;
                  return (
                    <div key={i} className="flex gap-3 p-3 hover:bg-slate-50 transition-colors">
                      <div className={`w-2 h-2 rounded-full mt-2 shrink-0 ${LEVEL_COLORS[n.level] || 'bg-slate-300'}`} />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-900 truncate">{n.title}</p>
                        <p className="text-xs text-slate-500 truncate">{n.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="relative w-72 h-full">
            <Sidebar onNavigate={() => setMobileOpen(false)} />
            <button onClick={() => setMobileOpen(false)} className="absolute top-4 right-[-40px] text-white bg-black/30 rounded-full p-1"><X className="w-5 h-5" /></button>
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden md:block"><Sidebar /></div>

      <main className="md:ml-72 min-h-screen pt-14 md:pt-0">
        <div className="max-w-[1600px] mx-auto p-4 md:p-6 lg:p-8">{children}</div>
      </main>
      <Toaster position="top-right" richColors />
    </div>
  );
}
